import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { Redirect } from 'react-router-dom';
import { ReportAnswer } from "../../models/enumerations/report-answer";
import { MskScoreVersion } from "../../models/enumerations/msk-score-versions";
import ReportWellness from "../../components/report/report-wellness";
import { ReportWellnessDataV1, ReportWellnessDataV2 } from "../../models/interfaces/reports/report-wellness-data-versioned";
import { PainArea } from "../../models/enumerations/pain-area";

const ReportWellnessTestPage: React.FC = () => {
    const { state } = useAuthState();
    const params = new URLSearchParams(window.location.search);
    const version = params.get("version");
    if (!state.claims.superAdmin) {
        return <Redirect push to="/" />;
    }

    const data: ReportWellnessDataV1 = {
        accountHolderEmail: "test@test.com",
        age: 28,
        ageRange: "25-34",
        behavioralHealth: { average: 89, percentage: 100 },
        behavioralHealthGraphData: { data: [85, 87, 89, 90], title: "Behavioral Health" },
        bodyComposition: {
            percentage: 20,
            average: 87,
            bmi: ReportAnswer.B,
            hipToWaistRatio: ReportAnswer.C
        },
        bodyCompositionGraphData: { data: [22, 21.5, 22.2, 22.5], title: "Body Composition" },
        breathingQuality: {
            percentage: 92,
            average: 90,
            breathBreath: ReportAnswer.A,
            breathingQuestionnaire: ReportAnswer.B,
            breathingSmokeNow: false
        },
        breathingQualityGraphData: { data: [88, 89, 90, 91], title: "Breathing Quality" },
        currentMonth: true,
        filterNames: [],
        focusAreas: ["INJURY_HISTORY", "MOVEMENT_HEALTH_BREATHING"],
        graphLabels: ["Jan", "Feb", "Mar", "Apr"],
        groupNames: ["test", "test2", "test3"],
        highRisk: false,
        highRiskType: "PAIN",
        injuryHistory: {
            percentage: 90,
            average: 85,
            injuryHistoryInjury: true,
            injuryHistoryPain: false,
            injuryHistoryPainPhysician: false,
            injuryHistorySaneScore: 95,
        },
        injuryHistoryGraphData: { data: [0, 1, 0, 0], title: "Injury History" },
        isFemale: false,
        mskScoreSecondsCreated: 1748523058,
        movementFocusArea: "SPINE_CLEARING",
        movementFocusAreaDuration: 6,
        movementHealth: {
            percentage: 85,
            average: 80,
            toeTouchReachLeft: { itemOne: false, itemTwo: false },
            toeTouchReachRight: { itemOne: false, itemTwo: false },
            toeTouchQPain: ReportAnswer.A,
            toeTouchScore: 0,
            shoulderMobilityReachLeft: { itemOne: false, itemTwo: false },
            shoulderMobilityReachRight: { itemOne: false, itemTwo: false },
            shoulderMobilityQPain: ReportAnswer.B,
            shoulderMobilityScore: 4,
            rotationReachLeft: { itemOne: false, itemTwo: false },
            rotationReachRight: { itemOne: false, itemTwo: false },
            rotationQPain: ReportAnswer.A,
            rotationScore: 5,
            squatLow: { itemOne: false, itemTwo: false },
            squatQPain: ReportAnswer.A,
            squatScore: 5,
            firstBalanceReach: { itemOne: false, itemTwo: false },
            secondBalanceReachLeft: { success: false, attempts: 2 },
            secondBalanceReachRight: { success: false, attempts: 3 },
            thirdBalanceReachLeft: { success: false, attempts: 1 },
            thirdBalanceReachRight: { success: false, attempts: 2 },
            balanceQPain: ReportAnswer.A,
            balanceScore: 5,
            shoulderClearingScore: false,
            shoulderClearingQPain: ReportAnswer.A,
            spineClearingScore: false,
            spineClearingQPain: ReportAnswer.D
        },
        movementHealthGraphData: { data: [80, 82, 85, 87], title: "Movement Health" },
        movementPain: true,
        nutritionalAwareness: {
            percentage: 70,
            average: 65
        },
        nutritionAwarenessGraphData: { data: [75, 78, 80, 82], title: "Nutrition Awareness" },
        organizationName: "Org Name that can be Longer",
        painCorrective: false,
        physicalActivity: {
            percentage: 85,
            average: 80,
            physicalActivityModerate: ReportAnswer.C,
            physicalActivityVigorous: ReportAnswer.B,
            physicaActivityStrengthNum: ReportAnswer.A
        },
        physicalActivityGraphData: { data: [120, 130, 140, 150], title: "Physical Activity" },
        readableMovementFocusArea: "Mobility",
        reportDate: "2022-05-06",
        roadToWellnessFocusArea: true,
        sleepWellness: {
            percentage: 80,
            average: 75,
            sleepPSQI: ReportAnswer.B
        },
        sleepWellnessGraphData: { data: [6, 7, 7.5, 7], title: "Sleep Wellness" },
        symmioAccess: true,
        tagNames: ["FMS Instructors"],
        totalUsers: 20,
        userEmail: "user@test.com",
        userFullName: "Test User",
        wellnessGraphData: { data: [90, 91, 92, 93], title: "Wellness Score" },
        wellnessScore: 92
    }

    const data2: ReportWellnessDataV2 = {
        accountHolderEmail: "test@test.com",
        age: 28,
        ageRange: "25-34",
        behavioralHealth: { average: 89, percentage: 100 },
        behavioralHealthGraphData: { data: [85, 87, 89, 90], title: "Behavioral Health" },
        bodyComposition: {
            percentage: 20,
            average: 87,
            bmi: ReportAnswer.B,
            hipToWaistRatio: ReportAnswer.C
        },
        bodyCompositionGraphData: { data: [22, 21.5, 22.2, 22.5], title: "Body Composition" },
        breathingQuality: {
            percentage: 92,
            average: 90,
            breathBreath: ReportAnswer.A,
            breathingQuestionnaire: ReportAnswer.B,
            breathingSmokeNow: false
        },
        breathingQualityGraphData: { data: [88, 89, 90, 91], title: "Breathing Quality" },
        currentMonth: true,
        filterNames: [],
        focusAreas: ["INJURY_HISTORY", "MOVEMENT_HEALTH_BREATHING"],
        graphLabels: ["Jan", "Feb", "Mar", "Apr"],
        groupNames: ["test", "test2", "test3"],
        highRisk: false,
        highRiskType: "PAIN",
        injuryHistory: {
            percentage: 90,
            average: 85,
            injuryHistoryInjury: true,
            injuryHistoryPain: false,
            injuryHistoryPainPhysician: false,
            injuryHistorySaneScore: 95,
            injuryHistoryPainAreasAndScales: [{ area: PainArea.LeftHip, direction: "FRONT", scale: 10 },
            { area: PainArea.RightThigh, direction: "FRONT", scale: 10 },
            { area: PainArea.LeftThigh, direction: "FRONT", scale: 10 },
            { area: PainArea.RightKnee, direction: "FRONT", scale: 10 },
            { area: PainArea.LeftKnee, direction: "FRONT", scale: 10 },
            { area: PainArea.RightAnkle, direction: "FRONT", scale: 10 },
            { area: PainArea.LeftAnkle, direction: "FRONT", scale: 10 },
            { area: PainArea.LeftGlute, direction: "BACK", scale: 10 },
            { area: PainArea.RightGlute, direction: "BACK", scale: 10 },
            { area: PainArea.LeftGlute, direction: "BACK", scale: 10 },
            { area: PainArea.RightHamstring, direction: "BACK", scale: 10 },
            { area: PainArea.LeftCalf, direction: "BACK", scale: 10 },
            { area: PainArea.LeftAchilles, direction: "BACK", scale: 10 },
            { area: PainArea.RightAchilles, direction: "BACK", scale: 10 },
            ]
        },
        injuryHistoryGraphData: { data: [0, 1, 0, 0], title: "Injury History" },
        isFemale: false,
        mskScoreSecondsCreated: 1748523058,
        movementFocusArea: "SPINE_CLEARING",
        movementFocusAreaDuration: 6,
        movementHealth: {
            percentage: 85,
            average: 80,
            toeTouchReachLeft: { itemOne: false, itemTwo: false },
            toeTouchReachRight: { itemOne: false, itemTwo: false },
            toeTouchPainAreasAndScales: [{ area: "Left Shoulder", direction: "FRONT", scale: 1 },
            { area: PainArea.Chest, direction: "FRONT", scale: 9 },
            { area: PainArea.RightArm, direction: "FRONT", scale: 6 },
            { area: PainArea.LeftArm, direction: "FRONT", scale: 7 },
            { area: PainArea.RightWrist, direction: "FRONT", scale: 3 },
            { area: PainArea.LeftWrist, direction: "FRONT", scale: 2 },
            { area: PainArea.RightHip, direction: "FRONT", scale: 5 },],
            toeTouchScore: 0,
            shoulderMobilityReachLeft: { itemOne: false, itemTwo: false },
            shoulderMobilityReachRight: { itemOne: false, itemTwo: false },
            shoulderMobilityPainAreasAndScales: [{ area: PainArea.LeftShoulder, direction: "BACK", scale: 2 },
            { area: PainArea.RightShoulder, direction: "BACK", scale: 3 },
            { area: PainArea.Neck, direction: "BACK", scale: 5 },
            { area: PainArea.UpperBack, direction: "BACK", scale: 4 },
            { area: PainArea.LowerBack, direction: "BACK", scale: 6 },
            { area: PainArea.SiJoint, direction: "BACK", scale: 7 },
            { area: PainArea.LeftHamstring, direction: "BACK", scale: 3 },
            { area: PainArea.RightCalf, direction: "BACK", scale: 2 }],
            shoulderMobilityScore: 4,
            rotationReachLeft: { itemOne: false, itemTwo: false },
            rotationReachRight: { itemOne: false, itemTwo: false },
            rotationPainAreasAndScales: [{ area: PainArea.LeftShoulder, direction: "BACK", scale: 1 },
            { area: PainArea.RightShoulder, direction: "BACK", scale: 2 },
            { area: PainArea.Neck, direction: "BACK", scale: 3 },
            { area: PainArea.UpperBack, direction: "BACK", scale: 4 },
            { area: PainArea.LowerBack, direction: "BACK", scale: 5 },
            { area: PainArea.SiJoint, direction: "BACK", scale: 6 },
            { area: PainArea.LeftHamstring, direction: "BACK", scale: 7 },
            { area: PainArea.RightCalf, direction: "BACK", scale: 8 }],
            rotationScore: 5,
            squatLow: { itemOne: false, itemTwo: false },
            squatPainAreasAndScales: [{ area: PainArea.LeftShoulder, direction: "BACK", scale: 1 },
            { area: PainArea.RightShoulder, direction: "BACK", scale: 2 },
            { area: PainArea.Neck, direction: "BACK", scale: 3 },
            { area: PainArea.UpperBack, direction: "BACK", scale: 6 },
            { area: PainArea.LowerBack, direction: "BACK", scale: 7 },
            { area: PainArea.SiJoint, direction: "BACK", scale: 8 },
            { area: PainArea.LeftHamstring, direction: "BACK", scale: 9 },
            { area: PainArea.RightCalf, direction: "BACK", scale: 9 }],
            squatScore: 5,
            firstBalanceReach: { itemOne: false, itemTwo: false },
            secondBalanceReachLeft: { success: false, attempts: 2 },
            secondBalanceReachRight: { success: false, attempts: 3 },
            thirdBalanceReachLeft: { success: false, attempts: 1 },
            thirdBalanceReachRight: { success: false, attempts: 2 },
            balancePainAreasAndScales: [{ area: PainArea.LeftShoulder, direction: "BACK", scale: 1 },
            { area: PainArea.RightShoulder, direction: "BACK", scale: 2 },
            { area: PainArea.Neck, direction: "BACK", scale: 3 },
            { area: PainArea.UpperBack, direction: "BACK", scale: 4 },
            { area: PainArea.LowerBack, direction: "BACK", scale: 5 },
            { area: PainArea.SiJoint, direction: "BACK", scale: 6 },
            { area: PainArea.LeftHamstring, direction: "BACK", scale: 7 },
            { area: PainArea.RightCalf, direction: "BACK", scale: 8 }],
            balanceScore: 5,
            shoulderClearingScore: false,
            shoulderClearingPainAreasAndScales: [{ area: PainArea.LeftShoulder, direction: "BACK", scale: 1 },
            { area: PainArea.RightShoulder, direction: "BACK", scale: 2 },
            { area: PainArea.Neck, direction: "BACK", scale: 3 },
            { area: PainArea.UpperBack, direction: "BACK", scale: 4 },
            { area: PainArea.LowerBack, direction: "BACK", scale: 5 },
            { area: PainArea.SiJoint, direction: "BACK", scale: 6 },
            { area: PainArea.LeftHamstring, direction: "BACK", scale: 7 },
            { area: PainArea.RightCalf, direction: "BACK", scale: 8 }],
            spineClearingScore: false,
            spineClearingPainAreasAndScales: [{ area: PainArea.LeftShoulder, direction: "BACK", scale: 1 },
            { area: PainArea.RightShoulder, direction: "BACK", scale: 2 },
            { area: PainArea.Neck, direction: "BACK", scale: 3 },
            { area: PainArea.UpperBack, direction: "BACK", scale: 4 },
            { area: PainArea.LowerBack, direction: "BACK", scale: 5 },
            { area: PainArea.SiJoint, direction: "BACK", scale: 6 },
            { area: PainArea.LeftHamstring, direction: "BACK", scale: 7 },
            { area: PainArea.RightCalf, direction: "BACK", scale: 8 }]
        },
        movementHealthGraphData: { data: [80, 82, 85, 87], title: "Movement Health" },
        movementPain: true,
        mskVersion: MskScoreVersion.V2,
        nutritionalAwareness: {
            percentage: 70,
            average: 65
        },
        nutritionAwarenessGraphData: { data: [75, 78, 80, 82], title: "Nutrition Awareness" },
        organizationName: "Org Name that can be Longer",
        painCorrective: false,
        physicalActivity: {
            percentage: 85,
            average: 80,
            physicalActivityModerate: ReportAnswer.C,
            physicalActivityVigorous: ReportAnswer.B,
            physicaActivityStrengthNum: ReportAnswer.A
        },
        physicalActivityGraphData: { data: [120, 130, 140, 150], title: "Physical Activity" },
        readableMovementFocusArea: "Mobility",
        reportDate: "2022-05-06",
        roadToWellnessFocusArea: true,
        sleepWellness: {
            percentage: 80,
            average: 75,
            sleepPSQI: ReportAnswer.B
        },
        sleepWellnessGraphData: { data: [6, 7, 7.5, 7], title: "Sleep Wellness" },
        symmioAccess: true,
        tagNames: ["FMS Instructors"],
        totalUsers: 20,
        userEmail: "user@test.com",
        userFullName: "Test User",
        wellnessGraphData: { data: [90, 91, 92, 93], title: "Wellness Score" },
        wellnessScore: 92
    }
    switch (version) {
        case "2":
            return <ReportWellness data={data2} />;

        // Future use:
        // case "3":
        //     return <ReportWellness data={data3} />;

        case "1":
        default:
            return <ReportWellness data={data} />;
    }

}

export default ReportWellnessTestPage;
